import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, 
  Calendar, 
  BookOpen, 
  Sparkles, 
  Users, 
  BarChart3, 
  Settings, 
  LogOut,
  ChevronDown,
  Building2,
  UserCog,
  UserCheck,
  Wrench,
  Shield,
  TrendingUp,
  FileDown,
  Wallet,
  MessageSquare,
  CreditCard,
  Zap,
  Smartphone,
  Tablet,
  Building,
  Code,
  BarChart2,
  Plus
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import LanguageSelector from './LanguageSelector';
import PropertyFormModal from './PropertyFormModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    fetchProperties();
    
    // Update date every minute to keep it current
    const dateInterval = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(dateInterval);
  }, []);

  // Auto-refresh data at midnight to update to new business day
  useEffect(() => {
    const checkAndRefresh = () => {
      const now = new Date();
      const nextMidnight = new Date();
      nextMidnight.setHours(24, 0, 0, 0); // Next midnight
      
      const msUntilMidnight = nextMidnight - now;
      
      // Set timeout to refresh at midnight
      const timeoutId = setTimeout(() => {
        console.log('[Layout] New day detected - refreshing data...');
        fetchProperties();
        window.location.reload(); // Force refresh to update all components
      }, msUntilMidnight);
      
      return timeoutId;
    };
    
    const timeoutId = checkAndRefresh();
    
    // Also refresh every 5 minutes to prevent stale data
    const refreshInterval = setInterval(() => {
      fetchProperties();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(refreshInterval);
    };
  }, []);

  // Refresh data when user returns to tab after being away
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Layout] Tab became visible - refreshing properties...');
        fetchProperties();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        console.log('[Layout] No token found - redirecting to login');
        navigate('/login');
        return;
      }
      
      const timestamp = Date.now(); // Cache buster
      
      console.log('[Layout] Fetching properties...');
      
      const response = await axios.get(`${API}/properties?_t=${timestamp}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });
      
      console.log('[Layout] Properties fetched:', response.data.length);
      console.log('[Layout] Properties:', response.data.map(p => p.name));
      
      setProperties(response.data);
      
      // If no properties exist, clear selected property
      if (response.data.length === 0) {
        console.log('[Layout] No properties found - clearing selection');
        setSelectedProperty(null);
        localStorage.removeItem('selected_property');
        return;
      }
      
      const saved = localStorage.getItem('selected_property');
      if (saved) {
        const savedProp = JSON.parse(saved);
        console.log('[Layout] Checking if saved property exists:', savedProp.name);
        const exists = response.data.find(p => p.id === savedProp.id);
        if (exists) {
          console.log('[Layout] Found saved property, setting as selected');
          setSelectedProperty(exists);
        } else if (response.data.length > 0) {
          console.log('[Layout] Saved property not found, selecting first available');
          setSelectedProperty(response.data[0]);
          localStorage.setItem('selected_property', JSON.stringify(response.data[0]));
        }
      } else if (response.data.length > 0) {
        console.log('[Layout] No saved property, selecting first');
        setSelectedProperty(response.data[0]);
        localStorage.setItem('selected_property', JSON.stringify(response.data[0]));
      }
    } catch (error) {
      console.error('[Layout] Error fetching properties:', error);
      console.error('[Layout] Error response:', error.response?.data);
      
      // If token expired (401), it will be handled by axios interceptor
      // For other errors, keep current state to avoid jarring UX
      if (error.response?.status === 401) {
        console.log('[Layout] 401 error - will be handled by global interceptor');
      } else if (error.response?.status === 403) {
        console.log('[Layout] 403 Forbidden - insufficient permissions');
      } else {
        console.log('[Layout] Network or other error - keeping current state');
      }
    }
  };

  const handlePropertyChange = (property) => {
    setSelectedProperty(property);
    localStorage.setItem('selected_property', JSON.stringify(property));
    localStorage.setItem('keyless.activePropertyId', property.id); // Additional persistence
    window.location.reload();
  };

  const handlePropertyCreated = (newProperty) => {
    // Add new property to list and set as active
    fetchProperties(); // Refresh list
    setSelectedProperty(newProperty);
    localStorage.setItem('selected_property', JSON.stringify(newProperty));
    localStorage.setItem('keyless.activePropertyId', newProperty.id); // Additional persistence
    window.location.reload(); // Refresh to load property data
  };


  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/front-desk', icon: UserCheck, label: t('nav.frontDesk') },
    { path: '/calendar', icon: Calendar, label: t('nav.calendar') },
    { path: '/reservations', icon: BookOpen, label: t('nav.reservations') },
    { path: '/housekeeping', icon: Sparkles, label: t('nav.housekeeping') },
    { path: '/guests', icon: Users, label: t('nav.guests') },
    { path: '/reports', icon: BarChart3, label: t('nav.reports') },
    { path: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  const revenueNavItems = [
    { path: '/rate-plans', icon: BarChart3, label: 'Rate Plans' },
    { path: '/channel-manager', icon: BarChart3, label: 'Channel Manager' },
    { path: '/revenue-analytics', icon: BarChart3, label: 'Revenue Analytics' },
  ];

  const guestExperienceNavItems = [
    { path: '/addons', icon: BarChart3, label: 'Add-ons' },
    { path: '/promo-codes', icon: BarChart3, label: 'Promo Codes' },
    { path: '/messaging', icon: BarChart3, label: 'Messaging' },
  ];

  const operationsNavItems = [
    { path: '/staff', icon: UserCog, label: 'Staff Schedules' },
    { path: '/maintenance', icon: Wrench, label: 'Maintenance' },
    { path: '/audit-logs', icon: Shield, label: 'Audit Logs' },
    { path: '/forecasting', icon: TrendingUp, label: 'Forecasting' },
    { path: '/export-reports', icon: FileDown, label: 'Export Reports' },
  ];

  const integrationsNavItems = [
    { path: '/accounting', icon: Wallet, label: 'Accounting' },
    { path: '/communications', icon: MessageSquare, label: 'Communications' },
    { path: '/payment-diversity', icon: CreditCard, label: 'Payment Diversity' },
    { path: '/smart-systems', icon: Zap, label: 'Smart Systems' },
  ];

  const mobileNavItems = [
    { path: '/mobile-staff', icon: Smartphone, label: 'Mobile Staff' },
    { path: '/mobile-guest', icon: Smartphone, label: 'Mobile Guest' },
  ];

  const analyticsNavItems = [
    { path: '/advanced-analytics', icon: BarChart2, label: 'Advanced Analytics' },
    // API Marketplace removed - not customer-facing
  ];

  const securityNavItems = [
    { path: '/user-management', icon: Users, label: 'User Management' },
    { path: '/dnr', icon: Shield, label: 'Do Not Rent' },
  ];

  // Platform admin only
  const platformAdminNavItems = user?.role === 'platform_admin' ? [
    { path: '/platform-admin', icon: Shield, label: 'Platform Admin', badge: 'ADMIN' },
    { path: '/database-admin', icon: Building2, label: 'Database Admin', badge: 'DB' },
  ] : [];

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
            Keyless PMS
          </h1>
          <p className="text-xs text-slate-500 mt-1">Property Management</p>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Core Navigation */}
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 font-medium'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}

          {/* Revenue & Channels */}
          <div className="pt-4 border-t border-slate-200 mt-4">
            <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Revenue & Channels</p>
            {revenueNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Guest Experience */}
          <div className="pt-4 border-t border-slate-200 mt-4">
            <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Guest Experience</p>
            {guestExperienceNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Operations */}
          <div className="pt-4 border-t border-slate-200 mt-4">
            <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Operations</p>
            {operationsNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Integrations */}
          <div className="pt-4 border-t border-slate-200 mt-4">
            <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Integrations</p>
            {integrationsNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Mobile Apps */}
          <div className="pt-4 border-t border-slate-200 mt-4">
            <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Mobile</p>
            {mobileNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Analytics & BI */}
          <div className="pt-4 border-t border-slate-200 mt-4">
            <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Analytics & BI</p>
            {analyticsNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Security & Access (Owner/Manager only) */}
          {user && ['owner', 'manager'].includes(user.role) && (
            <div className="pt-4 border-t border-slate-200 mt-4">
              <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Security & Access</p>
              {securityNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 font-medium'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Platform Admin (Admin only) */}
          {platformAdminNavItems.length > 0 && (
            <div className="pt-4 border-t border-slate-200 mt-4">
              <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Platform Admin</p>
              {platformAdminNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 font-medium'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="flex-1">{item.label}</span>
                    {item.badge && (
                      <span className="px-2 py-0.5 text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white font-semibold">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
            </div>
          </div>
          <Button
            onClick={() => {
              onLogout();
              navigate('/logged-out');
            }}
            variant="outline"
            data-testid="logout-button"
            className="w-full justify-start gap-2"
            size="sm"
          >
            <LogOut size={16} />
            {t('btn.logout')}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="property-selector">
                  <Building2 size={18} />
                  <span>{selectedProperty ? selectedProperty.name : 'Select Property'}</span>
                  <ChevronDown size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {properties.map((property) => (
                  <DropdownMenuItem
                    key={property.id}
                    onClick={() => handlePropertyChange(property)}
                    data-testid={`property-option-${property.id}`}
                    className={selectedProperty?.id === property.id ? 'bg-slate-50' : ''}
                  >
                    <div>
                      <div className="font-medium">{property.name}</div>
                      <div className="text-xs text-slate-500">{property.address || 'No address'}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
                
                {/* Add Property Option */}
                {properties.length > 0 && (
                  <div className="border-t border-slate-200 my-1"></div>
                )}
                <DropdownMenuItem
                  onClick={() => setShowAddPropertyModal(true)}
                  className="text-cyan-600 font-medium"
                >
                  <Plus size={16} className="mr-2" />
                  Add Property
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSelector />
            <div className="text-sm text-slate-600">
              {currentDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long',
                day: 'numeric' 
              })}
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto p-6">
          <Outlet context={{ selectedProperty, refreshProperties: fetchProperties }} />
        </div>
      </main>
      
      {/* Add Property Modal */}
      <PropertyFormModal
        open={showAddPropertyModal}
        onClose={() => setShowAddPropertyModal(false)}
        mode="create"
        onSuccess={handlePropertyCreated}
      />
    </div>
  );
};

export default Layout;
